import React, { useState } from 'react';
import {
  CalendarIcon,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Loader2,
} from 'lucide-react';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { addMonths, format, startOfDay } from 'date-fns';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';

const cardData = [
  {
    name: 'medical device.bom',
    size: '10MB',
    img: 'C:\\Users\\kapoo\\OneDrive\\Pictures\\Screenshots\\Screenshot 2025-05-16 020933.png',
  },
  {
    name: 'Diagram file',
    size: '10MB',
    img: 'C:\\Users\\kapoo\\OneDrive\\Pictures\\Screenshots\\Screenshot 2025-05-16 020933.png',
  },
  {
    name: 'project.doc',
    size: '10MB',
    img: 'C:\\Users\\kapoo\\OneDrive\\Pictures\\Screenshots\\Screenshot 2025-05-16 020933.png',
  },
];

const formSchema = z.object({
  unit_price: z.number().min(1, 'Required'),
  total_price: z.number().min(1, 'Required'),
  min_order_quantity: z.number().min(1, 'Required'),
  lead_time: z.number().min(1, 'Required'),
  notes: z.string().optional(),
  quote_validity: z.date({
    required_error: 'Required',
    invalid_type_error: 'Invalid date',
  }),
});

const SupplierPartDetailScreen = ({ selectedPart, onClose, onSubmit }) => {
  const materialCategoryTable = selectedPart?.material_manufacturing_info || [];
  const testingMethodsTable = selectedPart?.testing_methods || [];

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const [showCalendar, setShowCalendar] = useState(false);

  const form = useForm({
    resolver: zodResolver(formSchema),
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    defaultValues: {
      unit_price: undefined,
      total_price: undefined,
      min_order_quantity: undefined,
      lead_time: undefined,
      notes: '',
      quote_validity: undefined,
    },
  });

  const disableInvalidDates = (date) => {
    const today = startOfDay(new Date());
    const sixMonthsFromNow = addMonths(today, 6);
    return date < today || date > sixMonthsFromNow;
  };

  const handleSubmit = (values) => {
    const formattedValues = {
      unit_price: values.unit_price,
      total_price: values.total_price,
      min_order_quantity: values.min_order_quantity,
      lead_time_days: values.lead_time,
      note_for_buyer: values.notes,
      quote_validity: values.quote_validity,
    };
    setIsLoading(true);

    try {
      onSubmit(formattedValues);
      // form.reset({
      //   unit_price: undefined,
      //   total_price: undefined,
      //   min_order_quantity: undefined,
      //   lead_time: undefined,
      //   notes: '',
      //   quote_validity: undefined,
      // });
    } catch (error) {
      console.error('Error submitting form:', error);
      setError(error.message || 'An error occurred while submitting the form.');
    } finally {
      setIsLoading(false);
      // onClose();
    }
  };

  return (
    <div>
      {/* Basic Information Section */}
      <div className="bg-gray-50 mt-6 rounded-lg p-4 px-6 mx-4">
        <div className="bg-gray-100 rounded-sm p-1 text-darkPurple font-semibold">
          Basic Information
        </div>

        <div className="flex gap-4 mt-4 h-full">
          <div className="flex flex-col flex-1">
            <div className="flex gap-20">
              <div>
                <div className="text-sm text-gray-500 w-64 font-semibold">
                  Part Name
                </div>
                <div className="bg-gray-100 p-2 rounded-sm">
                  {selectedPart?.part_name || '-'}
                </div>
              </div>
              <div>
                <div className="text-sm text-gray-500 w-64 font-semibold">
                  Part Number
                </div>
                <div className="bg-gray-100 p-2 rounded-sm">
                  {selectedPart?.part_number || '-'}
                </div>
              </div>
              <div>
                <div className="text-sm text-gray-500 w-64 font-semibold">
                  Part Revision
                </div>
                <div className="bg-gray-100 p-2 rounded-sm">
                  {selectedPart?.part_revision || '-'}
                </div>
              </div>
            </div>

            <div>
              <div className="mt-8 text-gray-500 text-sm font-semibold">
                Description
              </div>
              <div className="bg-gray-100 mt-2 p-2 rounded-sm">
                {selectedPart?.description || 'No description provided.'}
              </div>
            </div>
          </div>

          <div className="ml-4">
            <div className="text-gray-500 text-sm font-semibold">
              Part Image
            </div>
            <div className="w-40 h-full flex items-center justify-center rounded-md border border-gray-300 bg-white overflow-hidden">
              <img
                src="/api/placeholder/250/250"
                alt="Part"
                className="h-full w-auto object-contain"
              />
            </div>
          </div>
        </div>

        <div className="mt-7">
          <div className="text-gray-500 text-sm font-semibold">
            Lifecycle State
          </div>
          <div className="bg-gray-100 p-2 w-96 mt-2">
            {selectedPart?.life_cycle_state || '-'}
          </div>
        </div>
      </div>

      {/* Measurement & Quantity Section */}
      <div className="bg-gray-50 mt-6 rounded-lg p-4 px-6 mx-4 flex flex-col">
        <div className="bg-gray-100 rounded-sm p-1 text-darkPurple font-semibold">
          Measurement & Quantity
        </div>

        <div className="flex gap-2">
          <div className="flex flex-col mt-4">
            <div className="font-semibold text-gray-500 ">
              Usage Per Product
            </div>
            <div className="mt-2 bg-gray-100 p-2 w-80">
              {selectedPart?.usage_per_product || '-'}
            </div>
          </div>
          <div className="flex flex-col mt-4 mr-2">
            <div className="font-semibold text-gray-500 ">
              Total Number Of Products
            </div>
            <div className="mt-2 bg-gray-100 p-2">{'Work on this'}</div>
          </div>
          <div className="flex flex-col mt-4 mr-2">
            <div className="font-semibold text-gray-500 ">
              Total Required Parts
            </div>
            <div className="mt-2 bg-gray-100 p-2">
              {selectedPart?.total_parts_required || '-'}
            </div>
          </div>
          <div className="flex flex-col mt-4">
            <div className="font-semibold flex gap-1">
              <span className="text-gray-500 ">Precision</span>
              <span className="text-gray-400">(micron)</span>
            </div>
            <div className="mt-2 bg-gray-100 p-2 w-96">
              {selectedPart?.precision || '-'}
            </div>
          </div>
        </div>

        <div className="flex mt-4 gap-4">
          <div className="w-1/2">
            <div className="font-semibold text-gray-500 ">Tolerance</div>
            <div className="bg-gray-100 mt-2 w-full p-2 rounded">
              {selectedPart?.tolerance || '-'}
            </div>
          </div>
          <div className="">
            <div className="bg-gray-100 p-2 rounded mt-8 w-40">
              {selectedPart?.type_of_uom || '-'}
            </div>
          </div>
          <div className="">
            <div className="bg-gray-100 p-2 rounded mt-8 w-40">
              {selectedPart?.tolerance_uom || '-'}
            </div>
          </div>
        </div>

        <div className="mt-4 gap-4">
          <div className="font-semibold text-gray-500 ">Product Dimensions</div>
          <div className="flex gap-2">
            <div className="bg-gray-100 mt-2 w-96 p-2 rounded">
              <span>{selectedPart?.length}</span>
              <span className="text-gray-400 ml-1">(length)</span>
            </div>
            <div className="bg-gray-100 mt-2 w-96 p-2 rounded">
              <span>{selectedPart?.width}</span>
              <span className="text-gray-400 ml-1">(width)</span>
            </div>
            <div className="bg-gray-100 mt-2 w-96 p-2 rounded">
              <span>{selectedPart?.height}</span>
              <span className="text-gray-400 ml-1">(height)</span>
            </div>
            <div className="bg-gray-100 mt-2 w-48 p-2 rounded">
              <span>{selectedPart?.dimension_uom}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Material & Manufacturing Section */}
      <div className="bg-gray-50 mt-6 rounded-lg p-4 px-6 mx-4 flex flex-col">
        <div className="bg-gray-100 rounded-sm p-1 text-darkPurple font-semibold">
          Material & Manufacturing
        </div>
        <div>
          <table className="w-full border-separate border-spacing-y-6">
            <thead>
              <tr className="text-black text-md text-left bg-veryVeryLightPurple">
                <th className="px-4 py-3 whitespace-nowrap font-thin text-center rounded-l-lg border-t border-b border-l border-gray-300">
                  Material <br /> Category
                </th>
                <th className="px-4 py-3 text-center font-thin border-t border-b border-gray-300">
                  Required <br /> Material
                </th>
                <th className="px-4 py-3 whitespace-nowrap text-center font-thin border-t border-b border-gray-300">
                  Material Standard
                </th>
                <th className="px-4 py-3 text-center font-thin border-t border-b border-gray-300">
                  Material <br /> Compliance
                </th>
                <th className="px-4 py-3 font-thin border-t border-b border-gray-300">
                  Manufacturing <br /> Processes Needed
                </th>
                <th className="px-4 py-3 font-thin border-t border-b border-gray-300">
                  Surface Finish Process
                </th>
                <th className="px-4 py-3 font-thin border-t border-b border-gray-300">
                  Heat Treatment
                </th>
                <th className="px-4 py-3 font-thin rounded-r-lg border-t border-b border-r border-gray-300">
                  Uploads
                </th>
              </tr>
            </thead>
            <tbody>
              {materialCategoryTable.map((item, index) => (
                <tr
                  key={index}
                  className="bg-white text-sm text-black text-left rounded-lg"
                >
                  <td className="px-4 py-8 rounded-l-lg">{item.material}</td>
                  <td className="px-4 py-3">{item.req_mat}</td>
                  <td className="px-4 py-3 text-center">{item.mat_standard}</td>
                  <td className="px-4 py-3 text-center">
                    {item.mat_compliance}
                  </td>
                  <td className="px-4 py-3">{item.manu_process}</td>
                  <td className="px-4 py-3">{item.surface_finish}</td>
                  <td className="px-4 py-3">{item.heat_treatment}</td>
                  <td className="px-4 py-3">{item.upload}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Testing & Certification Section */}
      <div className="bg-gray-50 mt-6 rounded-lg p-4 px-6 mx-4 flex flex-col">
        <div className="bg-gray-100 rounded-sm p-1 text-darkPurple font-semibold">
          Testing & Certification
        </div>
        <div className="bg-gray-50 mt-6 border border-gray-300 rounded-lg px-4">
          <table className="w-full border-separate border-spacing-y-2">
            <thead>
              <tr className="text-black text-md text-left bg-veryVeryLightPurple">
                <th className="px-4 py-3 whitespace-nowrap font-thin text-left rounded-l-lg text-gray-500 ">
                  Test Method
                </th>
                <th className="px-4 py-3 font-thin rounded-r-lg border-t text-center text-gray-500 ">
                  Certification Required
                </th>
              </tr>
            </thead>
            <tbody>
              {testingMethodsTable.map((item, index) => (
                <tr
                  key={index}
                  className="bg-gray-50 text-sm text-black text-left rounded-lg"
                >
                  <td className="px-4 py-2 rounded-l-lg font-semibold">
                    {item.method}
                  </td>
                  <td className="px-4 py-2 text-center">
                    <input
                      type="checkbox"
                      className="w-6 h-6 accent-purple-600"
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Documents Attached Section */}
      <div className="bg-gray-50 mt-6 rounded-lg p-4 px-6 mx-4 flex flex-col">
        <div className="bg-gray-50 p-1 rounded text-darkerPurple font-semibold text-base">
          Documents Attached
        </div>
        <div className="flex gap-4">
          {cardData.map((cardItem, index) => (
            <div
              key={index}
              className="w-56 p-4 border border-gray-300 rounded-lg"
            >
              <div className="flex">
                <div className="px-2 py-4">
                  <img src="C:\Users\kapoo\OneDrive\Pictures\Screenshots\Screenshot 2025-05-16 020933.png"></img>
                </div>
                <img
                  className="block"
                  src="C:\Users\kapoo\OneDrive\Pictures\Screenshots\Screenshot 2025-05-16 020933.png"
                  alt=""
                />
                <div className="flex flex-col mb-3">
                  <div>
                    <p
                      className="font-medium text-sm truncate"
                      title={cardItem.name}
                    >
                      {cardItem.name}~
                    </p>
                    <p className="text-xs text-gray-500">{cardItem.size}</p>
                  </div>
                  <div className="flex flex-row justify-between">
                    <button className="text-darkPurple">Download</button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Note From Buyer Section */}
      <div className="bg-gray-50 mt-6 rounded-lg p-4 px-6 mx-4 flex flex-col">
        <div className="bg-gray-100 rounded-sm p-1 text-darkPurple font-semibold">
          Note From Buyer
        </div>
        <div className="p-2 mt-6 text-gray-500 ">
          {selectedPart?.additional_note || 'No notes provided by the buyer.'}
        </div>
      </div>

      {/* Your Quote Section */}
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(handleSubmit)}
          onKeyDown={(e) => {
            if (
              e.key === 'Enter' &&
              e.target.tagName === 'INPUT' &&
              e.target.tagName !== 'TEXTAREA' &&
              !e.target.getAttribute('data-allow-enter')
            ) {
              e.preventDefault();
            }
          }}
          className=""
        >
          <div className="bg-gray-50 mt-6 rounded-lg p-4 px-6 mx-4 flex flex-col">
            <div className="bg-veryVeryLightPurple text-black rounded-sm p-1 px-3 font-semibold">
              Your Quote
            </div>

            <div className="flex justify-between mt-10 transition-all duration-300 ease-in-out">
              <div className="flex flex-col gap-6">
                <FormField
                  control={form.control}
                  name="unit_price"
                  render={({ field }) => (
                    <FormItem className="flex-col gap-2 group ease-in duration-300">
                      <div className="flex items-center gap-2">
                        <FormLabel
                          className={`w-[250px] text-gray-600 ${form.formState.errors.unit_price ? 'text-red-500' : ''} group-hover:text-lightPurple`}
                        >
                          Unit Price ($)
                        </FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            type="number"
                            className={`focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple placeholder:text-gray-400 border border-gray-300 group-hover:border-lightPurple [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${
                              form.formState.errors.unit_price
                                ? 'border-red-500 focus:border-red-500'
                                : ''
                            } focus:ring-0 focus:outline-none`}
                            onChange={(e) => {
                              field.onChange(
                                e.target.value === ''
                                  ? ''
                                  : Number(e.target.value),
                              );
                            }}
                            onKeyDown={(e) => {
                              if (['e', 'E', '+', '-'].includes(e.key)) {
                                e.preventDefault();
                              }
                            }}
                            onWheel={(e) => e.target.blur()}
                            placeholder={`Enter Unit Price (e.g., $12.34)`}
                          />
                        </FormControl>
                      </div>

                      {/* Error message below the input */}
                      <FormMessage className="text-red-500 text-sm ml-[165px]" />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="min_order_quantity"
                  render={({ field }) => (
                    <FormItem className="flex-col gap-2 group ease-in duration-300">
                      <div className="flex items-center gap-2">
                        <FormLabel
                          className={`w-[250px] text-gray-600 ${form.formState.errors.min_order_quantity ? 'text-red-500' : ''} group-hover:text-lightPurple`}
                        >
                          Min Order Quantity (MOQ)
                        </FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            type="number"
                            className={`focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple placeholder:text-gray-400 border border-gray-300 group-hover:border-lightPurple [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${
                              form.formState.errors.min_order_quantity
                                ? 'border-red-500 focus:border-red-500'
                                : ''
                            } focus:ring-0 focus:outline-none`}
                            onChange={(e) => {
                              field.onChange(
                                e.target.value === ''
                                  ? ''
                                  : Number(e.target.value),
                              );
                            }}
                            onKeyDown={(e) => {
                              if (['e', 'E', '+', '-'].includes(e.key)) {
                                e.preventDefault();
                              }
                            }}
                            onWheel={(e) => e.target.blur()}
                            placeholder={`Enter MOQ (e.g., 100 units)`}
                          />
                        </FormControl>
                      </div>

                      {/* Error message below the input */}
                      <FormMessage className="text-red-500 text-sm ml-[165px]" />
                    </FormItem>
                  )}
                />
              </div>

              <div className="flex flex-col gap-6">
                <FormField
                  control={form.control}
                  name="total_price"
                  render={({ field }) => (
                    <FormItem className="flex-col gap-2 group ease-in duration-300">
                      <div className="flex items-center gap-2">
                        <FormLabel
                          className={`w-[250px] text-gray-600 ${form.formState.errors.total_price ? 'text-red-500' : ''} group-hover:text-lightPurple`}
                        >
                          Total Price ($)
                        </FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            type="number"
                            className={`focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple placeholder:text-gray-400 border border-gray-300 group-hover:border-lightPurple [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${
                              form.formState.errors.total_price
                                ? 'border-red-500 focus:border-red-500'
                                : ''
                            } focus:ring-0 focus:outline-none`}
                            onChange={(e) => {
                              field.onChange(
                                e.target.value === ''
                                  ? ''
                                  : Number(e.target.value),
                              );
                            }}
                            onKeyDown={(e) => {
                              if (['e', 'E', '+', '-'].includes(e.key)) {
                                e.preventDefault();
                              }
                            }}
                            onWheel={(e) => e.target.blur()}
                            placeholder={`Enter Total Price (e.g., $124.34)`}
                          />
                        </FormControl>
                      </div>

                      {/* Error message below the input */}
                      <FormMessage className="text-red-500 text-sm ml-[165px]" />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="lead_time"
                  render={({ field }) => (
                    <FormItem className="flex-col gap-2 group ease-in duration-300">
                      <div className="flex items-center gap-2">
                        <FormLabel
                          className={`w-[250px] text-gray-600 ${form.formState.errors.lead_time ? 'text-red-500' : ''} group-hover:text-lightPurple`}
                        >
                          Lead Time (days)
                        </FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            type="number"
                            className={`focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple placeholder:text-gray-400 border border-gray-300 group-hover:border-lightPurple [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${
                              form.formState.errors.lead_time
                                ? 'border-red-500 focus:border-red-500'
                                : ''
                            } focus:ring-0 focus:outline-none`}
                            onChange={(e) => {
                              field.onChange(
                                e.target.value === ''
                                  ? ''
                                  : Number(e.target.value),
                              );
                            }}
                            onKeyDown={(e) => {
                              if (['e', 'E', '+', '-'].includes(e.key)) {
                                e.preventDefault();
                              }
                            }}
                            onWheel={(e) => e.target.blur()}
                            placeholder={`Enter Lead Time (e.g., 30 days)`}
                          />
                        </FormControl>
                      </div>

                      {/* Error message below the input */}
                      <FormMessage className="text-red-500 text-sm ml-[165px]" />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            <div className="mt-6">
              <FormField
                control={form.control}
                name="notes"
                render={({ field }) => (
                  <FormItem className="group ease-in duration-300 space-y-1">
                    <FormLabel className="text-gray-600 group-hover:text-lightPurple">
                      Notes for Buyer
                    </FormLabel>
                    <FormControl>
                      <Textarea
                        {...field}
                        placeholder=""
                        className="focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple placeholder:text-gray-400 border border-gray-300 group-hover:border-lightPurple"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="w-full mx-auto mt-4 bg-gray-50">
              <div className="bg-gray-50 rounded-lg">
                {/* <FormField
                  control={form.control}
                  name="quote_validity"
                  render={({ field }) => (
                    <FormItem className="mt-2 w-1/3 group space-y-1">
                      <FormLabel
                        className={`text-gray-600 group-hover:text-darkPurple  ${form.formState.errors.quote_validity ? 'text-red-500' : ''}`}
                      >
                        Quote Validity
                      </FormLabel>
                      <FormControl>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              className={`w-full flex justify-between group-hover:bg-veryLightPurple group-hover:bg-opacity-15 ease-in duration-150 group-hover:text-black ${
                                field.value ? '' : 'text-slate-500'
                              } font-normal border  ${
                                form.formState.errors.quote_validity
                                  ? 'border-red-500 focus:border-red-500'
                                  : ' border-gray-300'
                              }`}
                            >
                              {field.value
                                ? format(field.value, 'PPP')
                                : 'Select the date'}
                              <CalendarIcon
                                className={`ml-2 h-4 w-4  ${
                                  form.formState.errors.quote_validity
                                    ? 'text-red-500'
                                    : 'text-darkPurple'
                                }`}
                              />
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="z-[80]" align="start">
                            <Calendar
                              mode="single"
                              classNames={{
                                caption_label:
                                  'text-sm font-medium text-gray-600',
                                nav: 'space-x-1 flex items-center',
                                nav_button:
                                  'h-7 w-7 bg-transparent p-0 text-gray-500 hover:text-gray-700',
                                nav_button_previous: 'absolute left-1',
                                nav_button_next: 'absolute right-1',
                                table: 'w-full border-collapse space-y-1',
                                head_row:
                                  'flex bg-veryLightPurple bg-opacity-15 rounded',
                                head_cell:
                                  'text-gray-500 rounded-md w-9 font-normal text-[0.8rem] h-9 flex items-center justify-center',
                                row: 'flex w-full mt-2',
                                cell: 'relative p-0 text-center text-sm focus-within:relative focus-within:z-[900] [&:has([aria-selected])]:bg-veryVeryLightPurple',
                                day: 'h-9 w-9 p-0 font-normal text-gray-600 hover:bg-veryVeryLightPurple hover:text-darkerPurple',
                                day_selected:
                                  'bg-purple-100 text-purple-900 hover:bg-purple-200 hover:text-purple-900 focus:bg-purple-200 focus:text-purple-900',
                                day_today:
                                  'bg-gray-100 text-gray-900 font-semibold',
                                day_outside:
                                  'text-gray-400 opacity-50 hover:bg-transparent hover:text-gray-400 hover:cursor-default',
                                day_disabled:
                                  'text-gray-400 opacity-50 hover:bg-transparent cursor-not-allowed',
                                day_range_middle:
                                  'aria-selected:bg-veryVeryLightPurple aria-selected:text-purple-900',
                                day_hidden: 'invisible',
                              }}
                              selected={field.value}
                              onSelect={field.onChange}
                              disabled={disableInvalidDates}
                            />
                          </PopoverContent>
                        </Popover>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                /> */}
                <FormField
                  control={form.control}
                  name="quote_validity"
                  render={({ field }) => {
                    return (
                      <FormItem className="mt-2 w-1/3 group space-y-1">
                        <div className="flex-col gap-4 space-y-1">
                          <FormLabel
                            className={`w-[200px] text-gray-600 group-hover:text-lightPurple ${
                              form.formState.errors.quote_validity
                                ? 'text-red-500'
                                : ''
                            }`}
                          >
                            Quote Validity
                          </FormLabel>
                          <div className="relative w-full group">
                            <Input
                              value={
                                field.value ? format(field.value, 'PPP') : ''
                              }
                              onClick={() => setShowCalendar(!showCalendar)}
                              readOnly
                              className={`w-full flex justify-between group-hover:bg-veryLightPurple group-hover:bg-opacity-15 ease-in duration-150 group-hover:text-black focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple placeholder:text-gray-400 border border-gray-300 ${
                                field.value ? '' : 'text-slate-500'
                              } font-normal border  ${
                                form.formState.errors.quote_validity
                                  ? 'border-red-500 focus:border-red-500'
                                  : ' border-gray-300'
                              }`}
                              placeholder="Select date"
                            />
                            <CalendarIcon
                              className={`absolute right-2 top-2.5 h-4 w-4 ${
                                form.formState.errors.quote_validity
                                  ? 'text-red-500'
                                  : 'text-darkPurple'
                              }`}
                            />
                            {showCalendar && (
                              <div className="absolute z-[9999] mt-2 bg-white border border-gray-200 rounded-lg shadow-lg p-1">
                                <Calendar
                                  mode="single"
                                  selected={field.value}
                                  onSelect={(val) => {
                                    field.onChange(val);
                                    setShowCalendar(false);
                                  }}
                                  disabled={disableInvalidDates}
                                  classNames={{
                                    caption_label:
                                      'text-sm font-medium text-gray-600',
                                    nav: 'space-x-1 flex items-center',
                                    nav_button:
                                      'h-7 w-7 bg-transparent p-0 text-gray-500 hover:text-gray-700',
                                    nav_button_previous: 'absolute left-1',
                                    nav_button_next: 'absolute right-1',
                                    table: 'w-full border-collapse space-y-1',
                                    head_row:
                                      'flex bg-veryLightPurple bg-opacity-15 rounded',
                                    head_cell:
                                      'text-gray-500 rounded-md w-9 font-normal text-[0.8rem] h-9 flex items-center justify-center',
                                    row: 'flex w-full mt-2',
                                    cell: 'relative p-0 text-center text-sm focus-within:relative focus-within:z-[900] [&:has([aria-selected])]:bg-veryVeryLightPurple',
                                    day: 'h-9 w-9 p-0 font-normal text-gray-600 hover:bg-veryVeryLightPurple hover:text-darkerPurple',
                                    day_selected:
                                      'bg-purple-100 text-purple-900 hover:bg-purple-200 hover:text-purple-900 focus:bg-purple-200 focus:text-purple-900',
                                    day_today:
                                      'bg-gray-100 text-gray-900 font-semibold',
                                    day_outside:
                                      'text-gray-400 opacity-50 hover:bg-transparent hover:text-gray-400 hover:cursor-default',
                                    day_disabled:
                                      'text-gray-400 opacity-50 hover:bg-transparent cursor-not-allowed',
                                    day_range_middle:
                                      'aria-selected:bg-veryVeryLightPurple aria-selected:text-purple-900',
                                    day_hidden: 'invisible',
                                  }}
                                />
                              </div>
                            )}
                          </div>
                        </div>
                        <FormMessage className="" />
                      </FormItem>
                    );
                  }}
                />

                <div className="mt-6 mb-8 bg-white p-6 rounded-lg">
                  <h3 className="text-lg font-medium text-darkPurple mb-4">
                    Upload Documents
                  </h3>
                  <p className="text-gray-600 text-sm mb-2">
                    Upload any relevant files to provide additional context for
                    quotation.
                  </p>
                  <p className="text-gray-500 text-sm mb-4">
                    Supported file types: PDF, CAD, DOC. Max size: 20MB per
                    file.
                  </p>
                  <button className="bg-darkPurple text-white px-6 py-3 rounded-lg hover:bg-darkerPurple transition-colors flex items-center gap-2">
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                      />
                    </svg>
                    Upload Documents
                  </button>
                </div>
                <div className="mt-4 w-full flex gap-4 items-center justify-center">
                  <Button
                    variant="outline"
                    className={`w-[100px] text-base text-darkPurple border-darkPurple transition ease-in-out duration-300 hover:text-darkPurple`}
                    onClick={(e) => {
                      e.preventDefault();
                      onClose();
                    }}
                  >
                    Back
                  </Button>
                  <Button
                    variant="outline"
                    type="submit"
                    disabled={isLoading}
                    className={`w-[150px] text-base text-white hover:text-white transition ease-in-out duration-300 ${
                      isLoading
                        ? 'bg-gray-400 cursor-not-allowed'
                        : 'bg-darkPurple hover:bg-darkerPurple'
                    } flex items-center justify-center`}
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      'Save Quote'
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </form>
      </Form>
    </div>
  );
};

export default SupplierPartDetailScreen;
